/**
 * Three-digit military identifier helpers (מספר פנימי).
 *
 * Vehicles and soldiers alike carry a three-digit internal number — the number
 * painted on the door of a truck, the number a driver is called by on the net.
 * It is the identifier people actually type, so it has to survive being pasted
 * out of a dispatch sheet, a licence line or a free-text message.
 *
 * Extraction rule: a candidate is a run of *exactly* three digits, and it must
 * not be one group of a longer grouped number. `צ 12-345-67` therefore yields
 * nothing — `345` sits between two other digit groups — while
 * `צ 12-345-67 / 350` yields `350`.
 */

/** The canonical form: exactly three digits, `000`–`999`. */
export const MILITARY_ID_PATTERN = /^\d{3}$/;

/** Characters that join the groups of a single grouped number. */
const GROUP_SEPARATORS = new Set(['-', '/', '.', '–', '—']);

/**
 * Bidi control marks — LRM/RLM, embeddings and isolates — that ride along
 * with text copied out of an RTL document. Built from escapes on purpose:
 * these characters are invisible in an editor.
 */
const BIDI_MARKS = new RegExp('[\\u200E\\u200F\\u202A-\\u202E\\u2066-\\u2069]', 'g');

export function isMilitaryId(value: string | null | undefined): boolean {
  return typeof value === 'string' && MILITARY_ID_PATTERN.test(value);
}

/**
 * Normalises loose input to the canonical form: `7` → `007`, ` 042 ` → `042`.
 * Returns `null` when the input holds no usable id.
 */
export function formatMilitaryId(raw: string | number | null | undefined): string | null {
  if (raw === null || raw === undefined) return null;
  const digits = String(raw).replace(BIDI_MARKS, '').trim();
  if (!/^\d{1,3}$/.test(digits)) return null;
  return digits.padStart(3, '0');
}

/**
 * Every standalone three-digit id in the text, in order of appearance and
 * without duplicates.
 */
export function extractMilitaryIds(raw: string | null | undefined): string[] {
  if (!raw) return [];
  const text = String(raw).replace(BIDI_MARKS, '');
  const found: string[] = [];

  for (const match of text.matchAll(/\d+/g)) {
    const [run] = match;
    if (run.length !== 3) continue;

    const start = match.index ?? 0;
    const end = start + run.length;
    // A group of a longer number (`12-345-67`) is not an id of its own.
    if (isGroupedWith(text, start - 1, -1)) continue;
    if (isGroupedWith(text, end, 1)) continue;

    if (!found.includes(run)) found.push(run);
  }

  return found;
}

/** The first standalone three-digit id in the text, or `null`. */
export function extractMilitaryId(raw: string | null | undefined): string | null {
  return extractMilitaryIds(raw)[0] ?? null;
}

/**
 * The last digit group of a grouped military plate — `899-503` → `503`.
 *
 * A vehicle whose three-digit internal number the unit has not recorded is
 * still called by something, and on the net that is the tail of its מספר צ.
 * The roster shows it in place of the internal number, and the search
 * highlights on it. Returns `''` when the plate holds no digits at all.
 */
export function plateTail(plateNumber: string | null | undefined): string {
  const groups = String(plateNumber ?? '').match(/\d+/g);
  return groups ? groups[groups.length - 1] : '';
}

/** The two numbers a vehicle can be called by. Any stored record satisfies it. */
export interface VehicleNumberProbe {
  militaryId?: string | null;
  plateNumber?: string | null;
}

/**
 * The number to put on screen for a vehicle: its internal number when the unit
 * has recorded one, otherwise the tail of its מספר צ.
 *
 * Every screen that shows a vehicle by number goes through here, so a vehicle
 * whose internal number has not been painted in yet is never rendered as a
 * blank square.
 */
export function vehicleCallSign(vehicle: VehicleNumberProbe): string {
  const internal = String(vehicle.militaryId ?? '').trim();
  return internal || plateTail(vehicle.plateNumber);
}

/**
 * True when the character at `index` is a group separator with another digit
 * behind it, walking in `step` direction — i.e. the digit run we came from is
 * part of a grouped number such as a military plate.
 */
function isGroupedWith(text: string, index: number, step: 1 | -1): boolean {
  const separator = text[index];
  if (separator === undefined || !GROUP_SEPARATORS.has(separator)) return false;
  const neighbour = text[index + step];
  return neighbour !== undefined && /\d/.test(neighbour);
}
